import { useSelector } from 'react-redux';
import { ChangeEvent } from 'react';

import { REASONS } from 'constants/reasonCodesDB';
import { getReasonCodes } from 'reducers/reasonCodesDB';
import { getAllowedDifference, getShowExpected } from 'reducers/OpenCloseDay';
import {
  getCurrencyFormatterNoSymbol,
  getSetting,
} from 'reducers/configs/settings';
import { float, positive, toFixed } from 'components/FieldTypes/formatters';

import { getDifference, isWithinAllowedDifference } from '../utils';
import { TenderComponentProps } from '../OCDTypes';

function useTender({
  billsTotal,
  onChange,
  isMissingReason,
  type,
  expected,
  counted,
  reason,
  isFirst,
}: Omit<TenderComponentProps, 'name'>) {
  const reasons = useSelector(getReasonCodes(REASONS.EOD_VARIANCE));
  const showExpected = useSelector(getShowExpected);
  const allowedDifference = useSelector(getAllowedDifference);
  const CURR = useSelector(getCurrencyFormatterNoSymbol);
  const reasonRequired = useSelector(
    getSetting('touchpos_eod_reason_required'),
  );
  function getCountedOrBillsTotal() {
    if (type === 'CASH') {
      return counted === '' ? billsTotal : counted;
    }
    return counted;
  }
  function validateCounted() {
    if (!showExpected || !reasonRequired) {
      return false;
    }
    if (
      !isWithinAllowedDifference(
        expected,
        getCountedOrBillsTotal(),
        allowedDifference,
      )
    ) {
      return reason < 0;
    }
    return false;
  }
  return {
    showExpected,
    countedProps: {
      className: isFirst ? 'firstTender' : undefined,
      value: counted === '0.00' ? '' : counted,
      error: validateCounted(),
      onChange: (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        const formatter =
          expected < 0
            ? // (this comment pushes ts-ignore down a line and makes it work)
              // @ts-ignore
              float.and(toFixed(2))
            : // (this comment pushes ts-ignore down a line and makes it work)
              // @ts-ignore
              float.and(positive).and(toFixed(2));
        onChange(type, {
          counted: formatter(event.target.value),
          reason,
        });
      },
      placeholder:
        type === 'CASH' && Number(billsTotal) ? billsTotal : undefined,
    },
    expectedProps: {
      value: CURR.stringify(expected),
    },
    differenceProps: {
      value: CURR.stringify(getDifference(expected, getCountedOrBillsTotal()), {
        skipRounding: true,
      }),
    },
    reasonProps: {
      value: reason,
      error: isMissingReason,
      options: [{ name: '-', reasonID: -1 }, ...reasons],
      onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChange(type, {
          counted,
          reason: Number(event.target.value),
        }),
    },
  };
}

export default useTender;
